/**
 * The ids this module reads (E5 spec D65) — the TypeScript mirror of
 * backend/app/custom/compass/pricing/contract.py. The module binds to these
 * ids, not to a template id: a fork that keeps them keeps the pricing
 * screens; a definition without them gets the generated form and no card
 * (silent by design). Pure: no React, no fetch — the manifest imports
 * `matchesPropose` / `matchesDecide` eagerly.
 */
import type { TaskFieldRead, TaskRead } from '@/types/process'
import type { PricingInputs } from './figures'

export const STEP_PROPOSE = 'propose_rate'
export const STEP_DECIDE = 'price_decision'

export const EXIT_SUBMITTED = 'submitted'
export const EXIT_APPROVED = 'approved'
export const EXIT_REJECTED = 'rejected'
/** SA-1223: optional on the step (an older pinned version has no such exit), so `matchesDecide` never requires it (R15). */
export const EXIT_DECLINED = 'declined'

export const F = {
  priceOffered: 'price_offered',
  targetRate: 'target_acmi',
  targetReason: 'target_acmi_reason',
  customerTarget: 'customer_target_price',
  customerTargetHigh: 'customer_target_price_high',
  costEstimated: 'direct_operating_cost',
  costActual: 'direct_operating_cost_actual',
  blockHours: 'block_hours',
  costMinimum: 'cost_minimum',
  utilisation: 'utilisation_assumption',
  marketLow: 'market_low',
  marketHigh: 'market_high',
  studyReference: 'study_reference',
  studyLink: 'study_link',
  changeRequest: 'change_request',
  approvalGivenBy: 'approval_given_by',
  costSheet: 'cost_sheet',
} as const

export const PROPOSE_FORM_FIELDS: readonly string[] = [F.priceOffered, F.targetRate, F.targetReason, F.customerTarget]
export const DECIDE_FORM_FIELDS: readonly string[] = [F.changeRequest, F.approvalGivenBy]

/** Major currency units as typed on the template (`quote_amount` is the one
 * money field in minor units, and this module never reads it). */
export const MONEY_FIELDS: ReadonlySet<string> = new Set([
  F.priceOffered, F.targetRate, F.customerTarget, F.customerTargetHigh, F.costEstimated, F.costActual,
  F.costMinimum, F.marketLow, F.marketHigh,
])

/** Only for a label the server did not send. The words on screen come from
 * the definition: the pricing read's `labels`, a task's `form[].label` /
 * `needs[].label` — so "Pricing Team ACMI" is an overlay edit, not a code
 * change. */
export const FALLBACK_LABELS: Record<string, string> = {
  [F.priceOffered]: 'Price offered',
  [F.targetRate]: 'Target rate per block hour',
  [F.targetReason]: 'Why this rate was selected',
  [F.customerTarget]: 'Customer target price',
  [F.customerTargetHigh]: 'Customer target price, high',
  [F.costEstimated]: 'Direct operating cost, estimated',
  [F.costActual]: 'Direct operating cost, actual',
  [F.blockHours]: 'Block hours',
  [F.costMinimum]: 'Cost-based minimum per block hour',
  [F.utilisation]: 'Utilisation assumption (block hours per month)',
  [F.marketLow]: 'Market reference, low',
  [F.marketHigh]: 'Market reference, high',
  [F.studyReference]: 'Study reference',
  [F.studyLink]: 'Study link',
  [F.changeRequest]: 'What to change',
  [F.approvalGivenBy]: 'Approval given by',
  [F.costSheet]: 'Cost sheet',
}

export function labelFor(labels: Record<string, string> | null | undefined, id: string): string {
  return labels?.[id] || FALLBACK_LABELS[id] || id.replace(/_/g, ' ')
}

const DECIMAL = /^[+-]?(\d+\.?\d*|\.\d+)([eE][+-]?\d+)?$/

/** The mirror of `figures.to_number`: a number, or a numeric string a person's
 * form may have stored ("832,000"). A bool is never a number. The decimal
 * gate keeps `Number('0x10')` from reading what Python's `float()` refuses. */
export function toNumber(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value !== 'string') return null
  const text = value.replace(/,/g, '').trim()
  if (!DECIMAL.test(text)) return null
  const parsed = Number(text)
  return Number.isFinite(parsed) ? parsed : null
}

/** SA-1330: the customer's price, an amount or a range. The mirror of the
 * backend snapshot's rule: low is the low's number, else the high's; high is
 * the high's number only when both are set (a lone high reads as the amount);
 * a high below the low is kept as typed. */
export interface CustomerPrice { low: number | null; high: number | null }

export function customerPrice(values: Record<string, unknown>): CustomerPrice {
  const low = toNumber(values[F.customerTarget])
  const high = toNumber(values[F.customerTargetHigh])
  if (low === null) return { low: high, high: null }
  return { low, high }
}

export function isFilled(value: unknown): boolean {
  if (typeof value === 'string') return value.trim() !== ''
  return value !== null && value !== undefined
}

export function fieldValues(fields: TaskFieldRead[]): Record<string, unknown> {
  return Object.fromEntries(fields.map((f) => [f.id, f.value]))
}

export function fieldLabels(fields: TaskFieldRead[]): Record<string, string> {
  return Object.fromEntries(fields.map((f) => [f.id, f.label]))
}

/** Field ids → the twin's input keys. */
export function toPricingInputs(values: Record<string, unknown>): PricingInputs {
  return {
    price_offered: toNumber(values[F.priceOffered]),
    cost_estimated: toNumber(values[F.costEstimated]),
    cost_actual: toNumber(values[F.costActual]),
    block_hours: toNumber(values[F.blockHours]),
    target_rate: toNumber(values[F.targetRate]),
    cost_minimum: toNumber(values[F.costMinimum]),
    market_low: toNumber(values[F.marketLow]),
    market_high: toNumber(values[F.marketHigh]),
  }
}

/** The view can do its job: the contract's fields are on the form, its exits
 * exist, nothing is redacted (the generated form renders "Restricted" and
 * disables the exits — E5 spec §5.5) and no AI proposal needs decorating. */
export function carries(task: TaskRead, formIds: readonly string[], exits: readonly string[]): boolean {
  if (task.proposal) return false
  const onForm = new Set(task.form.map((f) => f.id))
  const names = new Set(task.exits.map((e) => e.name))
  if (!formIds.every((id) => onForm.has(id)) || !exits.every((name) => names.has(name))) return false
  return ![...task.form, ...task.needs].some((f) => f.redacted)
}

export function matchesPropose(task: TaskRead): boolean {
  return carries(task, PROPOSE_FORM_FIELDS, [EXIT_SUBMITTED])
}

export function matchesDecide(task: TaskRead): boolean {
  return carries(task, DECIDE_FORM_FIELDS, [EXIT_APPROVED, EXIT_REJECTED])
}

// ── SA-449: step 4's sectors (spec §7.1) — the mirror of
// backend/app/custom/compass/sectors.py (`COLUMNS`, `LIVE`, `POSITIONING`).
export const STEP_ROTATION = 'aircraft_rotation'
export const EXIT_DONE = 'done'
export const SECTORS = 'sectors'
/** The free text a record pinned to a pre-SA-449 version still holds (the mirror of `FIELD_ROTATION_TEXT`). */
export const ROTATION_TEXT = 'rotation_legs'
/** Step 4's aircraft type, a plain string (SA-1038), the mirror of pricing/contract.py's `FIELD_AIRCRAFT_TYPE`. */
export const AIRCRAFT_TYPE = 'aircraft_type'
/** Step 8's optional `priced_as` (SA-1203), the mirror of `FIELD_PRICED_AS`; never required. */
export const PRICED_AS = 'priced_as'
export const SECTOR_COLUMNS = ['from', 'to', 'flown_as', 'payload'] as const
export const FLOWN_AS = ['live', 'positioning'] as const
/** Step 4's needs the editor reads for its "From the intake" card and its seed. */
export const INTAKE = { origin: 'origin', destination: 'destination', cargoSize: 'cargo_size', window: 'needed_window' } as const

/** The rotation view can do its job: the form's `sectors` is the table with
 * exactly these columns and options, and `done` is offered (plus
 * `carries`' own rules). A task on a record pinned to the text version has
 * no `sectors` and keeps the generated form. */
export function matchesRotation(task: TaskRead): boolean {
  if (!carries(task, [SECTORS], [EXIT_DONE])) return false
  const sectors = task.form.find((f) => f.id === SECTORS)
  const columns = sectors?.columns ?? []
  const flownAs = columns.find((c) => c.id === 'flown_as')
  return sectors?.type === 'table'
    && columns.map((c) => c.id).join() === SECTOR_COLUMNS.join()
    && flownAs?.type === 'enum'
    && (flownAs.options ?? []).join() === FLOWN_AS.join()
}

// ── SA-1197: the cost sheet (spec §2.1), the mirror of pricing/contract.py ──
export const STEP_RATE_REFERENCES = 'acmi_references'
export const COST_SHEET = 'cost_sheet'
export const COST_SHEET_COLUMNS = [
  'kind', 'from', 'to', 'label', 'station', 'quantity', 'rate', 'reference_rate', 'value',
  'reference_value', 'source', 'as_of', 'note',
  'visit', 'category', 'source_kind', 'source_ref', // SA-1209: optional provenance
  'burn_ref', // SA-1232: the aircraft burn's spec row
] as const
export const COST_SHEET_KINDS = ['block_hours', 'fuel', 'charge', 'overflight'] as const
/** A column → the sibling holding what the pre-fill put there (SA-1196's reference columns). */
export const COST_SHEET_REFERENCES: Readonly<Record<string, string>> = { rate: 'reference_rate', value: 'reference_value' }
